<template>
  <!-- One line per pick, the same row as Picks' PickCard: rank, team and
       frozen line on the left, win % and edge on the right. -->
  <article
    class="flex items-center gap-2 bg-transparent px-2 py-1.5 transition-colors hover:bg-white/[0.025]"
  >
    <span
      class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-brand-green/50 bg-brand-green/10 text-[10px] font-black tabular-nums text-brand-green"
      :aria-label="`Pick ${position}`"
    >
      {{ position }}
    </span>

    <TeamLogoChip
      :team="team.name"
      :abbr="team.abbr"
      :logo="team.logo"
      size="xs"
    />

    <button
      type="button"
      class="flex min-w-0 flex-1 items-center gap-2 text-left"
      :aria-expanded="expanded"
      :aria-label="`${expanded ? 'Collapse' : 'Expand'} ${team.abbr} pick details`"
      @click="$emit('toggle')"
    >
      <span class="flex min-w-0 flex-1 items-baseline gap-1.5">
        <span class="shrink-0 text-sm font-bold text-white">{{ team.abbr }}</span>
        <span class="shrink-0 text-sm font-black tabular-nums text-white">{{ selectedLine }}</span>
        <!-- While following the top 5 (Home) the tag takes the matchup's
             place: NEW on a game the last swap brought in, YOURS on a pick
             the following never moves. -->
        <span
          v-if="tag"
          class="shrink-0 self-center rounded-md px-1.5 py-0.5 text-[9px] font-extrabold tracking-[0.1em]"
          :class="tag === 'new' ? 'bg-brand-green/10 text-brand-green' : 'bg-sky-300/10 text-sky-300'"
        >
          {{ tag === "new" ? "NEW" : "YOURS" }}
        </span>
        <span v-else class="truncate text-xs text-slate-400">{{ matchupLabel }}</span>
        <svg
          v-if="submitted"
          class="h-3 w-3 shrink-0 self-center text-slate-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
          aria-label="Locked"
          role="img"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M16 12V8a4 4 0 00-8 0v4m-1 0h10a1 1 0 011 1v6a1 1 0 01-1 1H6a1 1 0 01-1-1v-6a1 1 0 011-1z"
          />
        </svg>
      </span>

      <span class="flex shrink-0 items-center gap-1 text-[11px] font-medium tabular-nums">
        <!-- Win % and the band word, the same pair the card prints. It used
             to print "Edge" beside the consensus point gap, which is
             display-only and nothing ranks on. -->
        <template v-if="summary">
          <span :class="bandClass">{{ summary.win }}</span>
          <span class="text-slate-500">·</span>
          <span :class="bandClass">{{ summary.band }}</span>
          <template v-if="summary.tie">
            <span class="text-slate-500">·</span>
            <span class="text-slate-400">{{ summary.tie }}</span>
          </template>
        </template>
        <span v-else-if="edge.side" class="text-slate-400">Edge on {{ edgeTeamAbbr }}</span>
        <span v-else class="text-slate-500">No edge</span>
      </span>

      <svg
        class="h-4 w-4 shrink-0 text-slate-400 transition-transform"
        :class="expanded ? 'rotate-180' : ''"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <button
      type="button"
      class="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-rose-500/10 hover:text-rose-300 active:bg-rose-500/20"
      :aria-label="`Remove ${team.abbr} from your picks`"
      :title="`Remove ${team.abbr} pick`"
      @click="$emit('remove')"
    >
      <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </article>
</template>

<script>
import TeamLogoChip from "./TeamLogoChip.vue";
import { computeEdge, formatSigned } from "../utils/edge.js";
import { BAND_TEXT_CLASS, pickedSummary } from "../utils/verdictLine.js";

export default {
  name: "PickedGameRow",
  components: { TeamLogoChip },
  props: {
    game: { type: Object, required: true },
    expanded: { type: Boolean, default: false },
    position: { type: Number, required: true },
    tag: { type: String, default: null },
  },
  emits: ["toggle", "remove"],
  computed: {
    pickedSide() {
      return this.game.pick?.side || "home";
    },
    submitted() {
      return Boolean(this.game.pick?.submitted_at);
    },
    team() {
      return this.pickedSide === "home" ? this.game.homeTeam : this.game.awayTeam;
    },
    matchupLabel() {
      const opponent = this.pickedSide === "home" ? this.game.awayTeam : this.game.homeTeam;
      return `${this.pickedSide === "home" ? "vs" : "@"} ${opponent.abbr}`;
    },
    edge() {
      return computeEdge(this.game);
    },
    summary() {
      return pickedSummary(this.edge, this.pickedSide);
    },
    // Coloured by band, the same as the card: a Flat pick is not green.
    bandClass() {
      return BAND_TEXT_CLASS[this.summary?.bandKey] ?? "text-slate-300";
    },
    edgeTeamAbbr() {
      return this.edge.side === "home"
        ? this.game.homeTeam.abbr
        : this.game.awayTeam.abbr;
    },
    selectedLine() {
      const homeLine = this.game.fivePicksSpread;
      if (homeLine == null) return "—";
      return formatSigned(this.pickedSide === "home" ? homeLine : -homeLine);
    },
  },
};
</script>
